import L from "leaflet";
import { getStationById } from "../../domain/storage/stationsStore";
import { Station, StationStatus } from "../../domain/types";
import { formatData } from "../data/formattingData";
import { getLastUpdatedSampleTimestamp } from "../data/latestData";
import { getStatusManager } from "../state/statusLayers";
import { circleIcon, getAnimationContext, getDataIcon, getStationStatus } from "./circleIcon";
import { stationToMarker } from "./createMarker";
import { createPopup } from "./popup";

type MarkerState = {
    showText?: boolean;
    status?: StationStatus;
    valueText?: string;
    stationName?: string;
    dataIconKey?: string;
    lastTooltipHtml?: string;
    lastPopupHtml?: string;
    lastAnimProgress?: number;
};

function getState(m: L.Marker): MarkerState {
    const anyM = m as any;
    if (!anyM.__state) anyM.__state = {} as MarkerState;
    return anyM.__state as MarkerState;
}

function getRootEl(m: L.Marker): HTMLElement | null {
    return m.getElement() as HTMLElement | null;
}

function computeStatus(s: Station): MarkerState["status"] {
    const ts = getLastUpdatedSampleTimestamp(s).getTime();
    const status = getStationStatus(ts);
    return status;
}
function patchMarkerDom(m: L.Marker,s: Station,opts: { anim: ReturnType<typeof getAnimationContext>; status: MarkerState["status"]; showText: boolean }) {
    const el = getRootEl(m);
    if (!el) return;
    const state = getState(m);
    const { anim, status } = opts;

    const root = el.querySelector(".circle-icon") as HTMLElement | null;
    if (!root) return;
    if (state.status !== status) {
        root.classList.remove("circle-icon--fresh", "circle-icon--stale", "circle-icon--offline");
        root.classList.add(`circle-icon--${status}`);
        state.status = status;
    }

    const fg = el.querySelector(".speedo-fg") as SVGCircleElement | null;
    if (fg && anim?.hasAnim) {
        const circ = Number(fg.dataset.circumference || "0");
        const offset = (1 - anim.progress) * circ;
        if (state.lastAnimProgress !== anim.progress) {
            fg.style.strokeDashoffset = String(offset);
            if (status !== "offline" && anim.color) fg.setAttribute("stroke", anim.color);
            state.lastAnimProgress = anim.progress;
        }
    }

    const nextText = formatData(s, true);
    const textEl = el.querySelector(".marker-text.is-dim, .marker-text.fresh, .marker-text.stale, .marker-text.offline, .circle-icon .marker-text:last-of-type") as HTMLElement | null;
    if (textEl && state.valueText !== nextText) {
        textEl.textContent = nextText;
        textEl.classList.toggle("is-dim", status !== "fresh");
        state.valueText = nextText;
    }

    const nameEl = el.querySelector(".circle-icon > .marker-text:not(.is-dim)") as HTMLElement | null;
    if (nameEl && state.stationName !== s.Name) {
        nameEl.textContent = s.Name;
        state.stationName = s.Name;
    }

    const data = getDataIcon(s);
    const key = data.key;
    if (state.dataIconKey !== key) {
        const wrap = el.querySelector(".icon-wrap") as HTMLElement | null;
        if (wrap) wrap.innerHTML = status !== "offline" ? data.svg : "";
        state.dataIconKey = key;
    }
}

function updatePopupsAndTooltips(m: L.Marker, s: Station, showTooltip: boolean) {
    const state = getState(m);

    const nextPopup = createPopup(s);
    const hasPopup = !!m.getPopup();
    if (!hasPopup) {
        m.bindPopup(nextPopup, { offset: new L.Point(0, -20, true) });
        state.lastPopupHtml = nextPopup;
    } else if (state.lastPopupHtml !== nextPopup) {
        m.setPopupContent(nextPopup);
        state.lastPopupHtml = nextPopup;
    }

    if (showTooltip) {
        const nextTooltip = createPopup(s);
        const hasTooltip = !!m.getTooltip();
        if (!hasTooltip) {
            m.bindTooltip(nextTooltip, { direction: "top", offset: new L.Point(0, -20, true), sticky: true });
            state.lastTooltipHtml = nextTooltip;
        } else if (state.lastTooltipHtml !== nextTooltip) {
            m.setTooltipContent(nextTooltip);
            state.lastTooltipHtml = nextTooltip;
        }
    } else {
        if (m.getTooltip()) {
            m.unbindTooltip();
            state.lastTooltipHtml = undefined;
        }
    }
}
export function refreshMarker(s: Station, map: L.Map) {
    const m = stationToMarker.get(s.Id);
    if (!m) return;

    const showTextInfo = map.getZoom() >= 8;
    const state = getState(m);

    const nextStatus = computeStatus(s);
    const needsRebuild = state.showText !== showTextInfo || state.status !== nextStatus || !getRootEl(m);
    if (needsRebuild) {
        m.setIcon(circleIcon(s, showTextInfo));
        state.showText = showTextInfo;
        state.status = nextStatus;
        state.lastAnimProgress = undefined;
        state.dataIconKey = undefined;
        state.stationName = undefined;
        state.valueText = undefined;
    }

    const anim = getAnimationContext(s);
    patchMarkerDom(m, s, { anim, status: nextStatus, showText: showTextInfo });

    updatePopupsAndTooltips(m, s, showTextInfo);

    if (nextStatus) {
        getStatusManager().update(s.Id, nextStatus);
    }
}

const dirtyIds = new Set<string>();
let rafId = 0;
let currentMap: L.Map | null = null;

export function attachMapForBatching(map: L.Map) {
    currentMap = map;
}

export function markDirty(stationId: string) {
    dirtyIds.add(stationId);
    if (!rafId) {
        rafId = requestAnimationFrame(() => {
            const map = currentMap;
            rafId = 0;
            if (!map) {
                // Fallback: nothing to do
                dirtyIds.clear();
                return;
            }
            for (const id of dirtyIds) {
                const s = getStationById(id);
                if (s) refreshMarker(s, map);
            }
            dirtyIds.clear();
        });
    }
}

// ────────────────────────────────────────────────────────────────────────────────
// Do zoom-breakpoint rebuilds only on zoomend (not every refresh)
export function setupZoomBreakpointHandler(map: L.Map) {
    map.on("zoomStart", function () {
        map.dragging.disable();
    });
    map.on("zoomend", () => {
        map.dragging.enable();
        const showTextInfo = map.getZoom() >= 8;
        for (const [id, m] of stationToMarker) {
            const s = getStationById(id);
            if (!s) continue;
            const state = getState(m);
            if (state.showText !== showTextInfo || !getRootEl(m)) {
                m.setIcon(circleIcon(s, showTextInfo));
                state.showText = showTextInfo;
                // as above, reset fields impacted by structure
                state.lastAnimProgress = undefined;
                state.dataIconKey = undefined;
                state.stationName = undefined;
                state.valueText = undefined;

                // Tooltip binding/unbinding belongs here (structural)
                if (showTextInfo) {
                    if (!m.getTooltip()) {
                        m.bindTooltip(createPopup(s), {
                            direction: "top",
                            offset: new L.Point(0, -20, true),
                            sticky: true
                        });
                    }
                } else if (m.getTooltip()) {
                    m.unbindTooltip();
                }
            }
        }
    });
}